import { useState, useEffect } from 'react';
import { entityDiscoveryService } from '../services/entity-discovery.service';
import { EntitiesByApplicationResult, ApplicationGroup } from '../services/types/entity-discovery.types';

export interface ApplicationEntityInfo {
  key: string;
  name: string;
  isGenerated: boolean;
  entityCount: number;
  entities: {
    name: string;
    label: string;
    resourceName: string;
    isGenerated: boolean;
  }[];
}

export const useApplicationEntities = () => {
  const [applications, setApplications] = useState<ApplicationEntityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await entityDiscoveryService.getEntitiesByApplication();
      
      // Convert to array format for easier rendering
      const applicationList: ApplicationEntityInfo[] = Object.entries(result.applications)
        .map(([key, app]) => ({
          key,
          name: app.name,
          isGenerated: app.isGenerated,
          entityCount: app.entityCount,
          entities: app.entities.filter(entity => 
            // Only include entities that have REST controllers
            true // We already filtered this in the backend
          )
        }))
        // Filter out empty applications
        .filter(app => app.entities.length > 0)
        // Sort by application type and name
        .sort((a, b) => {
          // Business domain first, then other apps, framework last
          const order = { 'domain': 0, 'framework': 99 }; // Framework goes last
          const aOrder = order[a.key] ?? (a.isGenerated ? 3 : 2);
          const bOrder = order[b.key] ?? (b.isGenerated ? 3 : 2);
          
          if (aOrder !== bOrder) {
            return aOrder - bOrder;
          }
          
          return a.name.localeCompare(b.name);
        });
      
      setApplications(applicationList);
      console.log('Loaded application entities:', applicationList.map(app => 
        `${app.name} (${app.entityCount} entities)`
      ));
    } catch (err) {
      console.error('Failed to load application entities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const refreshApplications = async () => {
    await loadApplications();
  };

  // Get all entities flattened (for backward compatibility)
  const getAllEntities = () => {
    return applications.flatMap(app => 
      app.entities.map(entity => ({
        ...entity,
        applicationContext: app.key,
        applicationName: app.name
      }))
    );
  };

  return {
    applications,
    loading,
    error,
    refreshApplications,
    getAllEntities,
    totalApplications: applications.length,
    totalEntities: applications.reduce((sum, app) => sum + app.entityCount, 0)
  };
};
